/**
 * Сводка расходов кредитов по проектам в личном кабинете.
 */
import {Link} from 'react-router-dom';
import {UserCabinetNavbar} from '@common/ui/navigation/user-cabinet-navbar';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {Trans} from '@common/i18n/trans';
import {Button} from '@common/ui/buttons/button';
import {ColumnConfig} from '@common/datatable/column-config';
import {DataTable} from '@common/datatable/data-table';
import {FormattedDate} from '@common/i18n/formatted-date';
import {FormattedCurrency} from '@common/i18n/formatted-currency';
import {FormattedNumber} from '@common/i18n/formatted-number';
import {ArrowBackIcon} from '@common/icons/material/ArrowBack';
import {OpenInNewIcon} from '@common/icons/material/OpenInNew';

const dateTimeOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
};

export interface ProjectCreditUsageSummaryRow {
  id: string;
  kind?: 'site' | 'studio' | 'chat';
  chat_id?: string | null;
  project: {id: number; name: string | null; url: string | null} | null;
  credits_spent_total: number;
  amount_spent_total: number;
  currency: string;
  last_transaction_at: string | null;
}

function projectDetailsPath(row: ProjectCreditUsageSummaryRow): string | null {
  if (row.kind === 'chat' && row.chat_id) {
    return `/account-settings/credits/transactions/chats/${row.chat_id}`;
  }
  const id = row.project?.id;
  if (!id) {
    return null;
  }
  if (row.kind === 'studio') {
    return `/account-settings/credits/transactions/studio-projects/${id}`;
  }
  return `/account-settings/credits/transactions/projects/${id}`;
}

const columns: ColumnConfig<ProjectCreditUsageSummaryRow>[] = [
  {
    key: 'project',
    allowsSorting: false,
    minWidth: 'min-w-220',
    header: () => <Trans message="Project" />,
    body: row => {
      const detailsPath = projectDetailsPath(row);
      const label = row.project?.name ? (
        row.project.name
      ) : (
        <span className="text-muted">
          <Trans message="Project deleted" />
        </span>
      );
      return (
        <div className="flex min-w-0 items-center gap-10">
          {detailsPath ? (
            <Link className="min-w-0 truncate text-primary hover:underline" to={detailsPath}>
              {label}
            </Link>
          ) : (
            <span className="text-muted">—</span>
          )}
          {row.kind === 'studio' ? (
            <span className="shrink-0 text-xs text-muted">
              <Trans message="Studio" />
            </span>
          ) : null}
          {row.kind === 'chat' ? (
            <span className="shrink-0 text-xs text-muted">
              <Trans message="Chat" />
            </span>
          ) : null}
          {row.project?.url ? (
            <a
              href={row.project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted hover:text-primary"
              title={row.project.url}
            >
              <OpenInNewIcon size="xs" />
            </a>
          ) : null}
        </div>
      );
    },
  },
  {
    key: 'credits_spent_total',
    allowsSorting: false,
    header: () => <Trans message="Credits spent" />,
    body: row => <FormattedNumber value={row.credits_spent_total} />,
  },
  {
    key: 'amount_spent_total',
    allowsSorting: false,
    minWidth: 'min-w-140',
    header: () => <Trans message="Amount spent" />,
    body: row => (
      <FormattedCurrency value={row.amount_spent_total} currency={row.currency} />
    ),
  },
  {
    key: 'last_transaction_at',
    allowsSorting: false,
    header: () => <Trans message="Last transaction at" />,
    body: row =>
      row.last_transaction_at ? (
        <FormattedDate date={row.last_transaction_at} options={dateTimeOptions} />
      ) : (
        <span className="text-muted">—</span>
      ),
  },
];

function EmptyCreditTransactionsState({isFiltering}: {isFiltering: boolean}) {
  return (
    <div className="text-muted">
      {isFiltering ? (
        <Trans message="No projects match your search." />
      ) : (
        <Trans message="No project usage yet." />
      )}
    </div>
  );
}

export function CreditTransactionsHistoryPage() {
  return (
    <div className="min-h-screen bg-alt">
      <StaticPageTitle>
        <Trans message="Credits by project and currency" />
      </StaticPageTitle>
      <UserCabinetNavbar />
      <div className="container mx-auto px-24 py-24">
        <div className="mb-24">
          <Button
            variant="text"
            color="primary"
            size="sm"
            startIcon={<ArrowBackIcon />}
            elementType={Link}
            to="/account-settings"
          >
            <Trans message="Back to account settings" />
          </Button>
        </div>
        <h1 className="mb-12 text-3xl">
          <Trans message="Credits by project and currency" />
        </h1>
        <p className="mb-32 text-base text-muted">
          <Trans message="View summarized credits and amount spent per project and currency." />
        </p>
        <DataTable<ProjectCreditUsageSummaryRow>
          endpoint="me/credit-transactions"
          columns={columns}
          hideSearch
          enableSelection={false}
          collapseTableOnMobile={false}
          emptyStateMessage={
            <EmptyCreditTransactionsState isFiltering={false} />
          }
        />
      </div>
    </div>
  );
}
